import { Button, Modal } from "antd";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import exportElementToPdf from "../../lib/exportInvoicePdf";
import { useToast } from "../../../../contexts/ToastContext";
import ReservationInvoiceDocument from "../reservationDetails/invoice/ReservationInvoiceDocument";

const MODAL_MAX_WIDTH = 720;
const MOBILE_HORIZONTAL_INSET = 16;

const ReservationInvoiceModal = ({
    open,
    onClose,
    resDetails,
    customerDetails,
    carDetails,
    insurancePolicyData,
    stepper,
}) => {
    const { t } = useTranslation("reservations");
    const { myCompanyData } = useUser();
    const toast = useToast();
    const printRef = useRef(null);
    const [printLoader, setPrintLoader] = useState(false);
    const [pdfLoader, setPdfLoader] = useState(false);

    const reservationId = resDetails?.id || "invoice";
    const documentTitle = `rental-invoice-${reservationId}`;

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle,
        pageStyle: `@page { size: A4; margin: 12mm; }`,
    });

    const onPrint = () => {
        setPrintLoader(true);
        handlePrint();
        setTimeout(() => setPrintLoader(false), 1500);
    };

    const onDownloadPdf = async () => {
        if (!printRef.current) return;

        setPdfLoader(true);
        try {
            await exportElementToPdf(printRef.current, `${documentTitle}.pdf`);
        } catch {
            toast.error({ message: t("invoice.pdfError") });
        } finally {
            setPdfLoader(false);
        }
    };

    const modalWidth = `min(${MODAL_MAX_WIDTH}px, calc(100vw - ${MOBILE_HORIZONTAL_INSET * 2}px))`;

    const footer = (
        <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto sm:justify-end sm:gap-2">
            <Button key="close" className="!h-10 !w-full !rounded-xl sm:!w-auto" onClick={onClose}>
                {t("cancel")}
            </Button>
            <Button
                key="pdf"
                className="!h-10 !w-full !rounded-xl sm:!w-auto"
                loading={pdfLoader}
                onClick={onDownloadPdf}>
                {t("invoice.downloadPdf")}
            </Button>
            <Button
                key="print"
                className="!h-10 !w-full !rounded-xl sm:!w-auto"
                type="primary"
                loading={printLoader}
                onClick={onPrint}>
                {t("invoice.print")}
            </Button>
        </div>
    );

    return (
        <Modal
            open={open}
            title={<span className="fontSemi text-base text-[#2F2F46] sm:text-lg">{t("invoice.title")}</span>}
            onCancel={onClose}
            footer={footer}
            width={modalWidth}
            centered
            destroyOnClose
            classNames={{
                wrapper: "!p-4",
                content: "!flex !max-h-[95vh] !flex-col !overflow-hidden !p-0",
                header: "!mb-0 !shrink-0 !border-b !border-[#f0f0f0] !px-4 !py-3 sm:!px-5",
                body: "!min-h-0 !flex-1 !overflow-y-auto !overflow-x-hidden !bg-white !px-3 !py-3 sm:!px-4 sm:!py-4",
                footer: "!shrink-0 !border-t !border-[#f0f0f0] !bg-white !px-4 !py-3 sm:!px-5",
            }}
            styles={{
                content: { maxHeight: "85vh" },
            }}>
            <div ref={printRef} className="reservation-invoice-print-area min-w-0">
                <ReservationInvoiceDocument
                    resDetails={resDetails}
                    customerDetails={customerDetails}
                    carDetails={carDetails}
                    insurancePolicyData={insurancePolicyData}
                    myCompanyData={myCompanyData}
                    stepper={stepper}
                    t={t}
                />
            </div>
        </Modal>
    );
};

ReservationInvoiceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    resDetails: PropTypes.object,
    customerDetails: PropTypes.object,
    carDetails: PropTypes.object,
    insurancePolicyData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    stepper: PropTypes.number,
};

export default ReservationInvoiceModal;
